import {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { chain, get, isEmpty, isString, trim } from "lodash";
import { RiAiGenerate2 } from "react-icons/ri";
import useFileUpload from "@/hooks/useFileUpload";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { useModal } from "@/contexts/ModalContext";
import { useShow } from "@/contexts/ShowContext";
import { useUser } from "@/contexts/UserContext";
import ScheduleModal from "@/components/modals/ScheduleModal";
import ArtifactEditorModal from "@/components/modals/ArtifactEditorModal";
import ArtifactLoaderModal from "@/components/modals/ArtifactLoaderModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import { DEFAULT_ARTIFACT_VISIBILITY } from "@kagami/shared/artifactVisibility";
import { DEFAULT_ARTIFACT_SCOPE } from "@kagami/shared/artifactScope";
import VoiceSettingsModal from "@/components/modals/VoiceSettingsModal";
import StructuringReasoningModal from "@/components/modals/StructuringReasoningModal";
import AssetPreviewModal from "@/components/modals/AssetPreviewModal";
import ImagePreviewModal from "@/components/modals/ImagePreviewModal";
import VideoPreviewModal from "@/components/modals/VideoPreviewModal";
import AudioPreviewModal from "@/components/modals/AudioPreviewModal";
import ModelSelectorDialog from "./ModelSelectorDialog";
import ArtifactRichInput from "@/components/text-editor/ArtifactRichInput";
import VisualInputsRow from "./VisualInputsRow";
import SettingInput from "./SettingInput";
import ActionBar from "./ActionBar";
import { toast } from "sonner";
import {
  SMART_MODE_STORAGE_KEY,
  DEFAULT_MODEL_STORAGE_KEY,
  VISUAL_TYPES,
  SETTING_TYPES,
  ELEMENTS_INPUT_TYPE_FOR,
  ELEMENTS_INPUT_TYPES,
  elementsMediaType,
} from "./constants";
import {
  isPendingUpload,
  pickerDisplayUrl,
  pickerSubmitUrl,
  pickerDedupeKey,
  isPickerValueFilled,
  isValueFilled,
} from "./pickerValues";
import {
  fetchAiModels,
  fetchShowCharacters,
  expandPrompt,
  createArtifact,
  updateArtifact,
  deleteArtifact,
} from "./AiModelInputApiClient";
import { FiMoreHorizontal, FiX } from "react-icons/fi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../ui/shadcn/dropdown-menu";
import { ChevronDown, ChevronLeft, Pencil } from "lucide-react";

// A brand-new artifact's name before the user renames or saves it — an actual
// value it's seeded with, not just a placeholder shown for an empty one.
const DEFAULT_ARTIFACT_NAME = "New Artifact";

/**
 * Self-contained generator panel. Holds the state for the whole panel and
 * composes the presentational rows:
 *  - `VisualInputsRow` — asset pickers (default mode only)
 *  - the prompt row(s) — mention editor in smart mode, plain textarea otherwise
 *  - `ActionBar` — model picker, settings, Smart toggle, Generate
 */
const AiModelInput = forwardRef(function AiModelInput(
  {
    onTaskSubmitted,
    storageKey = DEFAULT_MODEL_STORAGE_KEY,
    className = "",
    resolveAssetById,
    destinationFolder = null,
  },
  ref,
) {
  const { show } = useShow();
  const { user } = useUser();
  const { openModal } = useModal();
  const { runTask } = useKagamiBridge();
  const { uploadFile } = useFileUpload();

  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  // `smartPrompt` is fully decoupled from `modelData` — just the mention-notation
  // text typed into Smart mode's own editor, independent of the selected model's
  // inputs (no reconciliation against them, nothing derived from or merged into
  // `modelData`). It's sent to preview/generate as its own field alongside a
  // `smart` flag; expanding it into the actual payload is a backend concern.
  //
  // `modelData` is the user's manual input — prompt text, asset pickers,
  // settings — for whichever model is selected. Visual inputs and settings
  // always bind here, in both modes. A Preview response replaces it outright
  // (the backend always returns the full payload back, so there's nothing to
  // merge — see handleSmartPreview).
  const [smartPrompt, setSmartPrompt] = useState("");
  const [modelData, setModelData] = useState({});
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [totalGenerations, setTotalGenerations] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // Preview is a separate async flow from a generation, so it gets its own flag —
  // sharing `submitting` disabled the Generate button while a preview was running.
  const [previewing, setPreviewing] = useState(false);
  const [characters, setCharacters] = useState([]);
  // Bumped when a saved prompt is loaded, to force the prompt editor(s) to
  // re-seed from the freshly-set modelData (they only read `value` on mount).
  const [editorResetNonce, setEditorResetNonce] = useState(0);
  const [structuringReasoning, setStructuringReasoning] = useState([]);
  // Bumped after any prompt write so the `/` mention list refetches.
  const [promptsVersion, setPromptsVersion] = useState(0);
  // The saved artifact currently backing the editor (set on load or save-as).
  // While set, the "Save" (overwrite) action targets it. Cleared on model
  // change since the editor resets. Shape: { id, name } | null.
  const [loadedArtifact, setLoadedArtifact] = useState(null);
  // True while the editor is backed by an artifact (loaded, or started via
  // "New Artifact") rather than a one-off prompt. Drives artifact-specific UI.
  const [isEditingArtifact, setIsEditingArtifact] = useState(false);
  // The artifact's own text, edited in its own box so it never overwrites
  // (or gets overwritten by) the model's prompt input(s) in modelData.
  const [artifactContent, setArtifactContent] = useState("");
  // artifactContent as of the last load/new/save — compared against the live
  // value to know whether there are unsaved changes to warn about on Cancel.
  const [artifactBaseline, setArtifactBaseline] = useState("");
  // The artifact's name as edited inline in the title bar (double-click, or
  // the pencil icon). Mirrors artifactContent — live as you type, but never
  // sent to the server until Save/Save As; a blur just closes the input,
  // it doesn't persist anything.
  const [artifactName, setArtifactName] = useState(DEFAULT_ARTIFACT_NAME);
  const [artifactNameBaseline, setArtifactNameBaseline] = useState(
    DEFAULT_ARTIFACT_NAME,
  );
  // Whether the title bar shows the name as an editable input right now.
  const [editingArtifactName, setEditingArtifactName] = useState(false);
  // The artifact's visibility as edited via the ActionBar switch. Feeds the
  // no-dialog quick save (handleOverwriteArtifact) — the dialog-based create
  // flow (Save / Save As) keeps its own independent visibility control.
  const [artifactVisibility, setArtifactVisibility] = useState(
    DEFAULT_ARTIFACT_VISIBILITY,
  );
  const [artifactVisibilityBaseline, setArtifactVisibilityBaseline] = useState(
    DEFAULT_ARTIFACT_VISIBILITY,
  );
  // The artifact's scope as edited via the ActionBar dropdown. Mirrors
  // artifactVisibility — feeds the no-dialog quick save; a brand-new artifact
  // still picks its scope through the save dialog, not this dropdown.
  const [artifactScope, setArtifactScope] = useState(DEFAULT_ARTIFACT_SCOPE);
  const [artifactScopeBaseline, setArtifactScopeBaseline] = useState(
    DEFAULT_ARTIFACT_SCOPE,
  );
  // Smart mode swaps the plain prompt textarea for the mention editor and
  // exposes the prompt preview. Default mode is the plain textarea.
  const [smartMode, setSmartMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SMART_MODE_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    localStorage.setItem(SMART_MODE_STORAGE_KEY, String(smartMode));
  }, [smartMode]);

  // -------- Fetch models --------
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const data = await fetchAiModels();
        if (cancelled) return;
        setModels(data);
        const savedId = localStorage.getItem(storageKey);
        const initial =
          (savedId && data.find((m) => m._id === savedId)) || data[0];
        if (initial) {
          setSelectedModel(initial);
          initModelData(initial);
        }
      } catch (err) {
        console.error("Error fetching models:", err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initModelData = (model) => {
    const md = {};
    if (model?.inputs?.length) {
      model.inputs.forEach((input) => {
        md[input.prop] = input.default;
      });
    }
    setModelData(md);
  };

  // -------- Persist selection / re-init data on model change --------
  useEffect(() => {
    if (!selectedModel) return;
    localStorage.setItem(storageKey, selectedModel._id);

    // Reconcile modelData against the new model's inputs — carrying over
    // values for props the new model still has, falling back to its defaults
    // for the rest. smartPrompt isn't touched: it's independent of any
    // model's inputs, so there's nothing on it to reconcile.
    const reconcileToModel = (previous) => {
      const md = {};
      selectedModel.inputs?.forEach((input) => {
        const raw = previous[input.prop];
        const existing = raw === "" || raw == null ? undefined : raw;
        let value;
        if (input.type === "options" && Array.isArray(input.enum)) {
          value = input.enum.some((e) => e.key === existing)
            ? existing
            : input.default;
        } else {
          value = existing ?? input.default;
        }
        if (value !== undefined) md[input.prop] = value;
      });
      return md;
    };

    setModelData(reconcileToModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?._id]);

  // Clamp a totalGenerations carried over from a previous model down to this
  // one's own max_generations, so switching to a lower-capacity model can't
  // leave a request count it doesn't support selected.
  useEffect(() => {
    if (!selectedModel) return;
    setTotalGenerations((previous) =>
      Math.min(previous, selectedModel.max_generations ?? 1),
    );
  }, [selectedModel?._id, selectedModel?.max_generations]);

  // A loaded artifact is tied to the current model's editor; forget it when the
  // model changes so the overwrite target can't leak across models.
  useEffect(() => {
    setLoadedArtifact(null);
    setIsEditingArtifact(false);
    setArtifactContent("");
    setArtifactBaseline("");
    setArtifactName(DEFAULT_ARTIFACT_NAME);
    setArtifactNameBaseline(DEFAULT_ARTIFACT_NAME);
    setArtifactVisibility(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactVisibilityBaseline(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactScope(DEFAULT_ARTIFACT_SCOPE);
    setArtifactScopeBaseline(DEFAULT_ARTIFACT_SCOPE);
  }, [selectedModel?._id]);

  // -------- Fetch characters when needed --------
  const hasCharactersInput = !!selectedModel?.inputs?.some(
    (i) => i.type === "character",
  );
  useEffect(() => {
    if (!hasCharactersInput || !show?._id) return;
    const fetchCharacters = async () => {
      try {
        const data = await fetchShowCharacters(show._id);
        setCharacters(
          data
            .filter((c) => c.voice_ids?.length > 0)
            .map((c) => ({
              name: c.name,
              voiceId: c.voice_ids[0].voice_id,
              thumbnail_url: c.thumbnail_url,
            })),
        );
      } catch (err) {
        console.error("Error fetching characters:", err);
      }
    };
    fetchCharacters();
  }, [hasCharactersInput, show?._id]);

  // -------- Imperative handle: recall settings from an asset/generation --------
  // Lets parents (e.g. AssetsEditor) preset the panel from a previous task:
  //  - Select the matching model (looked up by id in the already-loaded list)
  //  - Apply the previously-used settings as modelData (the model-change
  //    effect uses `setModelData(prev => ...)` so the recalled values are
  //    preserved over each input's `default`)
  //  - Restore `smart`/`smartPrompt` the same way, independent of modelData
  // Holds a recall request whose model hasn't loaded yet (e.g. the panel was
  // mounted at the same time the request arrived). Applied by the effect below
  // once `models` is populated.
  const pendingRecallRef = useRef(null);

  const applyRecall = useCallback(
    ({ model, settings, smart, smartPrompt: recalledSmartPrompt }) => {
      if (model) setSelectedModel(model);

      if (settings && typeof settings === "object") {
        setModelData({ ...settings });
      }
      if (typeof smart === "boolean") setSmartMode(smart);
      if (typeof recalledSmartPrompt === "string") {
        setSmartPrompt(recalledSmartPrompt);
      }
      setEditorResetNonce((n) => n + 1);
    },
    [setModelData],
  );

  useImperativeHandle(
    ref,
    () => ({
      recallSettings: ({ modelId, settings, smart, smartPrompt }) => {
        const model = modelId ? models.find((m) => m._id === modelId) : null;
        // Model id given but not loaded yet — stash and apply when models arrive.
        if (modelId && !model) {
          pendingRecallRef.current = { modelId, settings, smart, smartPrompt };
          return;
        }
        applyRecall({ model, settings, smart, smartPrompt });
      },
    }),
    [models, applyRecall],
  );

  // Flush a deferred recall once the matching model becomes available.
  useEffect(() => {
    const pending = pendingRecallRef.current;
    if (!pending || models.length === 0) return;
    const model = models.find((m) => m._id === pending.modelId);
    if (!model) return;
    pendingRecallRef.current = null;
    applyRecall({
      model,
      settings: pending.settings,
      smart: pending.smart,
      smartPrompt: pending.smartPrompt,
    });
  }, [models, applyRecall]);

  // -------- Helpers --------
  const updateModelData = (key, value) =>
    setModelData((prev) => ({ ...prev, [key]: value }));

  const requiredFieldsFilled = () => {
    const inputs = selectedModel?.inputs;
    if (!inputs?.length) return true;
    const req = inputs.filter((i) => i.required === true);
    if (!req.length) return true;
    return req.every((i) => isValueFilled(modelData?.[i.prop], i.type));
  };

  /**
   * Walk modelData and replace any pending-upload values with the URLs from
   * actually uploading them now. We cache per-File results so the same File
   * referenced from multiple inputs only uploads once.
   */
  const uploadPendingAssets = async (data) => {
    const inputs = selectedModel?.inputs || [];
    const uploadCache = new Map();
    const upload = async (pending) => {
      if (uploadCache.has(pending.file)) return uploadCache.get(pending.file);
      const promise = uploadFile(pending.file, "temp").then((res) => {
        if (!res?.url) throw new Error("Upload failed");
        return res.url;
      });
      uploadCache.set(pending.file, promise);
      return promise;
    };
    // Rich `{ url, thumbnail_url }` values get flattened to a plain URL
    // string for submission; pending uploads are resolved by uploading.
    const flatten = async (v) => {
      if (isPendingUpload(v)) return upload(v);
      if (typeof v === "string") return v;
      return pickerSubmitUrl(v);
    };
    const result = { ...data };
    for (const input of inputs) {
      const v = result[input.prop];
      if (v == null) continue;
      if (
        input.type === "image" ||
        input.type === "video" ||
        input.type === "audio"
      ) {
        result[input.prop] = await flatten(v);
      } else if (
        input.type === "elements" ||
        input.type === "video_elements" ||
        input.type === "audio_elements"
      ) {
        if (!Array.isArray(v)) continue;
        const next = [];
        for (const item of v) next.push(await flatten(item));
        result[input.prop] = next;
      }
    }
    return result;
  };

  /**
   * Generate, for both modes. `payload` is always the non-smart `modelData` —
   * `smartPrompt` rides along as its own field alongside a `smart` flag so the
   * backend can tell which one to actually generate from.
   */
  const handleSubmit = async (schedule = false) => {
    if (!selectedModel || !show?._id) return;
    let run_at = null;
    if (schedule) {
      const dt = await openModal(<ScheduleModal />);
      if (!dt) return;
      run_at = dt;
    }
    try {
      setSubmitting(true);
      const resolvedPayload = await uploadPendingAssets(modelData);
      const groupId = crypto.randomUUID();
      for (let i = 0; i < totalGenerations; i++) {
        await runTask({
          modelId: selectedModel._id,
          payload: resolvedPayload,
          smartPrompt,
          smart: smartMode,
          showId: show._id,
          group: groupId,
          generate_assets: true,
          destination_folder: destinationFolder || null,
          ...(run_at && { run_at }),
        });
      }
      onTaskSubmitted?.();
    } catch (err) {
      console.error("Error submitting task:", err);
      toast.error("Failed to submit task");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Posts the same body `handleSubmit` sends to `runTask` (minus the per-submit
   * `group`/`run_at`) so the expanded prompt reflects the real request.
   *
   * @throws when the backend can't expand the prompt
   */
  const requestPromptPreview = (payload) =>
    expandPrompt(show._id, {
      modelId: selectedModel._id,
      payload,
      smartPrompt,
      smart: smartMode,
      generate_assets: true,
      destination_folder: destinationFolder || null,
    });

  const handleStructuringReasoning = (preview) => {
    const lines = chain(get(preview, "structuringReasoning", []))
      .filter(isString)
      .map(trim)
      .compact()
      .value();

    setStructuringReasoning(lines);
  };

  const handleOpenStructuringReasoning = () =>
    openModal(<StructuringReasoningModal reasoning={structuringReasoning} />);

  /**
   * Smart mode's Preview: expand the prompt, then hand the result to the default
   * view — expanded text in the textarea, resolved media in the asset tiles — and
   * switch to it. `smartPrompt` rides along as its own field (see
   * requestPromptPreview) rather than being merged into the request payload here
   * — expanding it into actual prop values is a backend concern. The response
   * always comes back as the full payload (backend spreads the request payload
   * under its own overrides), so it replaces modelData outright — no merge.
   *
   * @throws when the backend can't expand the prompt
   */
  const handleSmartPreview = async () => {
    if (!selectedModel || !show?._id) return;
    try {
      setPreviewing(true);
      handleStructuringReasoning(null);
      const resolvedPayload = await uploadPendingAssets(modelData);
      /** @throws */
      const preview = await requestPromptPreview(resolvedPayload);
      handleStructuringReasoning(preview);
      setModelData(preview.aiModelPrompt);
      setSmartMode(false);
    } catch (err) {
      console.error("Error previewing prompt:", err);
      toast.error(err.message || "Failed to preview the prompt");
    } finally {
      setPreviewing(false);
    }
  };

  // Returns whether an artifact was actually created, so callers (e.g. the
  // Cancel/Done confirmation flow) know whether it's safe to stop editing.
  const handleSaveArtifact = async () => {
    const text = artifactContent.trim();
    if (!text) {
      toast.warning("Write a prompt before saving");
      return false;
    }
    const result = await openModal(
      <ArtifactEditorModal
        prompt={{
          name: artifactName,
          scope: artifactScope,
          visibility: artifactVisibility,
        }}
      />,
    );
    if (!result?.name) return false;
    try {
      // Adopt the new artifact as the loaded one so subsequent edits can be
      // saved over it via the overwrite action.
      const created = await createArtifact(show._id, {
        name: result.name,
        prompt: text,
        scope: result.scope,
        visibility: result.visibility,
      });
      if (created?._id)
        setLoadedArtifact({
          id: created._id,
          name: created.name,
          scope: created.scope,
          visibility: created.visibility,
          user: created.user,
        });
      setPromptsVersion((n) => n + 1);
      setArtifactBaseline(text);
      // Bring name/scope/visibility (and their baselines) in line with what
      // was actually saved — the dialog can set any of these to something
      // other than whatever the title bar / ActionBar held, and without this
      // `artifactHasChanges` would stay stuck true forever, since the
      // baseline never moved off the pre-save default.
      setArtifactName(result.name);
      setArtifactNameBaseline(result.name);
      setArtifactScope(result.scope);
      setArtifactScopeBaseline(result.scope);
      setArtifactVisibility(result.visibility);
      setArtifactVisibilityBaseline(result.visibility);
      toast.success("Artifact saved");
      return true;
    } catch (err) {
      console.error("Error saving prompt:", err);
      toast.error("Failed to save artifact");
      return false;
    }
  };

  // Quick-save the currently-loaded artifact with the editor's current
  // content, under its existing name/scope/visibility — no dialog. Returns
  // whether the save actually succeeded (see handleSaveArtifact above).
  const handleOverwriteArtifact = async () => {
    if (!loadedArtifact) return false;
    const text = artifactContent.trim();
    if (!text) {
      toast.warning("Write a prompt before saving");
      return false;
    }
    const name = artifactName.trim();
    if (!name) {
      toast.warning("Give the artifact a name before saving");
      return false;
    }
    try {
      await updateArtifact(show._id, loadedArtifact.id, {
        name,
        scope: artifactScope,
        visibility: artifactVisibility,
        prompt: text,
      });
      // Keep the cached name/scope/visibility in sync — visibility feeds the
      // ownership-lock check (loadedArtifact.visibility === Public), the rest
      // is cache hygiene since only their own controls read them back.
      setLoadedArtifact({
        ...loadedArtifact,
        name,
        visibility: artifactVisibility,
        scope: artifactScope,
      });
      setArtifactName(name);
      setArtifactNameBaseline(name);
      setPromptsVersion((n) => n + 1);
      setArtifactBaseline(text);
      setArtifactVisibilityBaseline(artifactVisibility);
      setArtifactScopeBaseline(artifactScope);
      toast.success(`Saved “${name}”`);
      return true;
    } catch (err) {
      console.error("Error saving artifact:", err);
      toast.error("Failed to save artifact");
      return false;
    }
  };

  // Save button in the artifact actions block: overwrite when editing an
  // already-loaded artifact, otherwise save the current content as a new one.
  const handleSaveArtifactChanges = () =>
    loadedArtifact ? handleOverwriteArtifact() : handleSaveArtifact();

  const artifactHasChanges =
    isEditingArtifact &&
    (artifactContent !== artifactBaseline ||
      artifactName !== artifactNameBaseline ||
      artifactVisibility !== artifactVisibilityBaseline ||
      artifactScope !== artifactScopeBaseline);

  const exitArtifactEditing = () => {
    setArtifactContent("");
    setArtifactBaseline("");
    setArtifactName(DEFAULT_ARTIFACT_NAME);
    setArtifactNameBaseline(DEFAULT_ARTIFACT_NAME);
    setEditingArtifactName(false);
    setArtifactVisibility(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactVisibilityBaseline(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactScope(DEFAULT_ARTIFACT_SCOPE);
    setArtifactScopeBaseline(DEFAULT_ARTIFACT_SCOPE);
    setLoadedArtifact(null);
    setIsEditingArtifact(false);
  };

  // Guards any action that would abandon the artifact currently being edited
  // (Cancel/Done, loading a different artifact, ...). Nothing to lose: proceed
  // immediately. Otherwise confirm — Discard proceeds without saving. A
  // brand-new artifact or one the current user owns gets the normal Save
  // (quick overwrite, or the save dialog for a new one); a loaded artifact
  // owned by someone else can't be overwritten, so it gets Save As (the save
  // dialog, saving a copy) instead. Dismissing the confirmation (its own
  // Cancel, or Escape) blocks the action so editing continues.
  const confirmDiscardArtifactChanges = async () => {
    if (!artifactHasChanges) return true;

    const ownsLoadedArtifact =
      !loadedArtifact || user?.id === loadedArtifact.user?.id;

    let saved = null;
    const interacted = await openModal(
      <ConfirmModal
        title="Unsaved changes"
        message="This artifact has unsaved changes. Save them before leaving?"
        buttons={[
          { label: "Discard", type: "danger-ghost", onClick: () => {} },
          ownsLoadedArtifact
            ? {
                label: "Save",
                type: "primary",
                onClick: async () => {
                  saved = await handleSaveArtifactChanges();
                },
              }
            : {
                label: "Save As",
                type: "primary",
                onClick: async () => {
                  saved = await handleSaveArtifact();
                },
              },
        ]}
      />,
    );
    if (!interacted) return false; // dismissed — keep editing
    if (saved === false) return false; // save was attempted and failed/cancelled — keep editing

    return true;
  };

  // Adopts an already-fetched artifact record (from the loader modal, or a
  // double-clicked mention chip) as the one backing the editor.
  const loadArtifactIntoEditor = (artifact) => {
    const text = artifact.prompt || "";
    const name = artifact.name || "";
    const visibility = artifact.visibility || DEFAULT_ARTIFACT_VISIBILITY;
    const scope = artifact.scope || DEFAULT_ARTIFACT_SCOPE;
    setArtifactContent(text);
    setArtifactBaseline(text);
    setArtifactName(name);
    setArtifactNameBaseline(name);
    setEditingArtifactName(false);
    setArtifactVisibility(visibility);
    setArtifactVisibilityBaseline(visibility);
    setArtifactScope(scope);
    setArtifactScopeBaseline(scope);
    setEditorResetNonce((n) => n + 1);
    setLoadedArtifact({
      id: artifact._id,
      name: artifact.name,
      scope: artifact.scope,
      visibility: artifact.visibility,
      user: artifact.user,
    });
    setIsEditingArtifact(true);
  };

  const handleLoadArtifact = async () => {
    if (!(await confirmDiscardArtifactChanges())) return;
    const picked = await openModal(<ArtifactLoaderModal />);
    if (!picked) return;
    loadArtifactIntoEditor(picked);
  };

  // Double-clicking an artifact mention chip in either prompt editor loads it
  // here, the same way picking it from the Load Artifact modal would.
  const handleLoadArtifactMention = async (artifact) => {
    if (!(await confirmDiscardArtifactChanges())) return;
    loadArtifactIntoEditor(artifact);
  };

  const handleNewArtifact = async () => {
    if (!(await confirmDiscardArtifactChanges())) return;
    setArtifactContent("");
    setArtifactBaseline("");
    setArtifactName(DEFAULT_ARTIFACT_NAME);
    setArtifactNameBaseline(DEFAULT_ARTIFACT_NAME);
    setEditingArtifactName(false);
    setArtifactVisibility(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactVisibilityBaseline(DEFAULT_ARTIFACT_VISIBILITY);
    setArtifactScope(DEFAULT_ARTIFACT_SCOPE);
    setArtifactScopeBaseline(DEFAULT_ARTIFACT_SCOPE);
    setEditorResetNonce((n) => n + 1);
    setLoadedArtifact(null);
    setIsEditingArtifact(true);
  };

  const handleCancelArtifact = async () => {
    if (!(await confirmDiscardArtifactChanges())) return;
    exitArtifactEditing();
  };

  const handleDeleteArtifact = async () => {
    if (!loadedArtifact) return;
    const confirmed = await openModal(
      <ConfirmModal
        title="Delete Artifact"
        message={`Are you sure you want to delete “${loadedArtifact.name}”? This cannot be undone.`}
        type="destructive"
      />,
    );
    if (!confirmed) return;
    try {
      await deleteArtifact(show._id, loadedArtifact.id);
      setPromptsVersion((n) => n + 1);
      toast.success(`Deleted “${loadedArtifact.name}”`);
      exitArtifactEditing();
    } catch (err) {
      console.error("Error deleting artifact:", err);
      toast.error("Failed to delete artifact");
    }
  };

  /**
   * Opens the right preview modal for a picker value. Already-uploaded URL
   * strings go through the rich AssetPreviewModal; pending (local) files use
   * the lightweight Image/Video/AudioPreviewModal so we don't need a full
   * asset record (which we don't have until upload).
   */
  const handlePreviewPickerValue = (value, mediaType) => {
    if (isPendingUpload(value)) {
      const asset = {
        url: value.previewUrl,
        thumbnail_url: value.previewUrl,
        name: value.name || "",
        mediaType,
      };
      if (mediaType === "video") openModal(<VideoPreviewModal video={asset} />);
      else if (mediaType === "audio")
        openModal(<AudioPreviewModal audio={asset} />);
      else openModal(<ImagePreviewModal image={asset} />);
      return;
    }
    const url = pickerSubmitUrl(value);
    if (!url) return;
    const asset = {
      url,
      thumbnail_url: pickerDisplayUrl(value) || url,
      name: "",
      mediaType,
    };
    openModal(<AssetPreviewModal asset={asset} />);
  };

  // -------- Categorize inputs --------
  const inputs = selectedModel?.inputs || [];
  const visualInputs = inputs.filter((i) => VISUAL_TYPES.includes(i.type));
  const textInputs = inputs.filter((i) => i.type === "text");
  const settingInputs = inputs.filter((i) => SETTING_TYPES.includes(i.type));

  /**
   * Routes a multi-asset drop across all visual inputs by mediaType.
   *
   * - The picker that physically received the drop (`targetInput`) gets first
   *   pick of any assets matching its own type — replacing single-value
   *   pickers and appending to elements-style pickers.
   * - Every remaining asset is then placed into the *best* input for its
   *   mediaType: elements arrays are preferred (since they hold many),
   *   falling back to a same-type single picker only if that slot is empty.
   * - Assets with no matching input on the current model are reported via a
   *   toast so the user knows nothing happened to them.
   */
  // Pure: takes a payload and returns a new one with the assets written into the
  // matching visual inputs. Used by the drag-drop path (distributeAssetsToInputs).
  const withAssetsDistributed = useCallback(
    (payload, draggedAssets, targetInput) => {
      const toValue = (a) =>
        a.thumbnail_url
          ? { url: a.url, thumbnail_url: a.thumbnail_url }
          : a.url;

      {
        const prev = payload;
        const updates = { ...prev };
        const consumed = new Set();
        const handled = new Set();

        const writeToInput = (input, assets, { replace }) => {
          const mt = elementsMediaType(input.type);
          const matching = assets.filter(
            (a) => a?.mediaType === mt && a?.url && !consumed.has(a),
          );
          if (matching.length === 0) return;
          if (ELEMENTS_INPUT_TYPES.has(input.type)) {
            const current = Array.isArray(updates[input.prop])
              ? updates[input.prop]
              : [];
            const seen = new Set(
              current.map(pickerDedupeKey).filter((k) => k != null),
            );
            const max = input.max || 8;
            const remaining = Math.max(0, max - current.length);
            const taken = [];
            for (const a of matching) {
              if (taken.length >= remaining) break;
              if (seen.has(a.url)) continue;
              seen.add(a.url);
              taken.push(a);
            }
            if (taken.length === 0) return;
            updates[input.prop] = [...current, ...taken.map(toValue)];
            taken.forEach((a) => consumed.add(a));
          } else {
            if (!replace && isPickerValueFilled(updates[input.prop])) return;
            updates[input.prop] = toValue(matching[0]);
            consumed.add(matching[0]);
          }
        };

        if (targetInput && VISUAL_TYPES.includes(targetInput.type)) {
          writeToInput(targetInput, draggedAssets, { replace: true });
          handled.add(targetInput.prop);
        }

        const remainingTypes = [
          ...new Set(
            draggedAssets
              .filter((a) => !consumed.has(a))
              .map((a) => a?.mediaType)
              .filter(Boolean),
          ),
        ];
        for (const mt of remainingTypes) {
          const group = draggedAssets.filter(
            (a) => a.mediaType === mt && !consumed.has(a),
          );
          const elementsType = ELEMENTS_INPUT_TYPE_FOR[mt];
          const elementsInput = elementsType
            ? visualInputs.find(
                (i) => i.type === elementsType && !handled.has(i.prop),
              )
            : null;
          const singleInput = visualInputs.find(
            (i) => i.type === mt && !handled.has(i.prop),
          );
          const best = elementsInput || singleInput;
          if (!best) continue;
          writeToInput(best, group, { replace: false });
          handled.add(best.prop);
        }

        return updates;
      }
    },
    [visualInputs],
  );

  const distributeAssetsToInputs = useCallback(
    (draggedAssets, targetInput) =>
      setModelData((prev) =>
        withAssetsDistributed(prev, draggedAssets, targetInput),
      ),
    [setModelData, withAssetsDistributed],
  );

  return (
    <div id="AiModelInput" className={`flex flex-col h-full ${className}`}>
      <div className="flex items-center mb-[-1px] z-[1]">
        <div className="flex gap-2 ">
          {isEditingArtifact ? (
            <div className="flex items-start rounded-t-md border border-b-0 overflow-hidden bg-card/95">
              <button
                id="ActionBar__backArtifact"
                type="button"
                title="Close"
                onClick={handleCancelArtifact}
                className="select-none inline-flex h-7 pl-3 pr-1 outline-none items-center justify-center text-xs bg-card/95 hover:text-foreground text-foreground/60 cursor-pointer">
                <FiX className="w-3 h-3" />
              </button>
              <div
                id="AiModelInput__loadArtifact"
                className="group min-w-32 flex items-center justify-between gap-2 h-7 rounded-bl-md px-2 bg-card/95 hover:bg-secondary/80 text-xs text-secondary-foreground max-w-60">
                {editingArtifactName ? (
                  <input
                    id="AiModelInput__artifactNameInput"
                    type="text"
                    autoFocus
                    value={artifactName}
                    onChange={(e) => setArtifactName(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    // Blur just closes the input — it never saves. The actual
                    // name change only reaches the server via Save/Save As,
                    // same as every other artifact edit (content, scope, ...).
                    // An emptied field falls back to the default rather than
                    // leaving artifactName as "" — it's a real value the rest
                    // of the flow (the save dialog, the quick-save guard)
                    // relies on being non-blank, not just a display fallback.
                    onBlur={() => {
                      if (!artifactName.trim())
                        setArtifactName(DEFAULT_ARTIFACT_NAME);
                      setEditingArtifactName(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "Escape") {
                        e.currentTarget.blur();
                      }
                    }}
                    className="min-w-0 flex-1 bg-transparent text-xs text-secondary-foreground outline-none"
                  />
                ) : (
                  <span
                    id="AiModelInput__artifactTitle"
                    className="truncate cursor-text select-none"
                    title="Double-click to rename"
                    onDoubleClick={() => setEditingArtifactName(true)}>
                    {artifactName ? artifactName : "New Artifact"}
                  </span>
                )}
                {!editingArtifactName && (
                  <button
                    id="AiModelInput__renameArtifact"
                    type="button"
                    title="Rename artifact"
                    onClick={() => setEditingArtifactName(true)}
                    className="shrink-0 hidden group-hover:inline-flex items-center justify-center text-secondary-foreground/70 hover:text-foreground cursor-pointer">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button
              id="AiModelInput__modelPicker"
              type="button"
              onClick={() => setModelsDialogOpen(true)}
              className="min-w-32 flex items-center gap-3 h-7 pl-3 pr-2 rounded-t-md border border-b-0 bg-card/95 hover:bg-secondary/80 text-xs text-secondary-foreground cursor-pointer max-w-60 truncate">
              <span className="truncate">
                {selectedModel?.name || "Select model"}
              </span>
              <ChevronDown className="w-4 h-4 text-secondary-foreground/70" />
            </button>
          )}
        </div>
        <div className="flex flex-1 h-full gap-1 items-start justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                id="AiModelInput__artifactMenu"
                type="button"
                title="Artifacts"
                className="select-none inline-flex h-6 w-6 outline-none items-center justify-center text-xs rounded-sm border bg-background hover:bg-secondary text-foreground cursor-pointer">
                <FiMoreHorizontal className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                id="AiModelInput__artifactMenu_newArtifact"
                className="text-xs p-1"
                onClick={handleNewArtifact}>
                New Artifact
              </DropdownMenuItem>
              <DropdownMenuItem
                id="AiModelInput__artifactMenu_loadArtifact"
                className="text-xs p-1"
                onClick={handleLoadArtifact}>
                Load Artifact
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="relative flex flex-col gap-1 bg-card/95 border rounded-b-lg rounded-tr-sm h-full overflow-hidden border-secondary">
        {/* Row 1: visual inputs (each label + asset picker(s) in a horizontal row).
                  Smart mode has no pickers — its media comes from asset mentions.
                  Editing an artifact has no pickers either — it isn't tied to a
                  model's inputs, only to its own text (see Row 2). */}
        {!isEditingArtifact && !smartMode && visualInputs.length > 0 && (
          <VisualInputsRow
            inputs={visualInputs}
            modelData={modelData}
            resolveAssetById={resolveAssetById}
            onChange={updateModelData}
            onAssetDrop={distributeAssetsToInputs}
            onPreview={handlePreviewPickerValue}
          />
        )}

        {/* Row 2: prompt(s). `flex-1 min-h-0` so the textarea absorbs any
                  extra vertical space when the user resizes the panel upward.
                  Editing an artifact swaps every text input for a single box
                  bound to `artifactContent`, kept apart from the model's own
                  prompt input(s) in modelData so neither overwrites the other.
                  Smart mode swaps them all for one editor bound to the
                  independent `smartPrompt` string — it isn't tied to any of
                  the model's text inputs. */}
        {isEditingArtifact ? (
          <div
            id="AiModelInput__artifactPrompt"
            className={`flex flex-1 min-h-0 flex-col gap-1 px-3 ${!visualInputs.length ? "pt-3" : "pt-2"}`}>
            {/* Smart mode is a generation-payload concept (mention notation the
                worker expands) — the toggle is hidden while editing an artifact
                and its value shouldn't gate which editor renders here. */}
            <ArtifactRichInput
              className="flex-1"
              resetKey={`artifact:${loadedArtifact?.id || "new"}:${editorResetNonce}`}
              promptsVersion={promptsVersion}
              resolveAsset={resolveAssetById}
              value={artifactContent}
              onChange={setArtifactContent}
              onSubmit={handleSaveArtifactChanges}
              onLoadArtifactMention={handleLoadArtifactMention}
            />
          </div>
        ) : smartMode ? (
          <div
            id="AiModelInput__smartPrompt"
            className={`flex flex-1 min-h-0 flex-col gap-1 px-3 ${!visualInputs.length ? "pt-3" : "pt-2"}`}>
            <ArtifactRichInput
              className="flex-1"
              resetKey={`${selectedModel?._id || "none"}:smart:${editorResetNonce}`}
              promptsVersion={promptsVersion}
              resolveAsset={resolveAssetById}
              value={smartPrompt}
              onChange={setSmartPrompt}
              onSubmit={() => handleSubmit(false)}
              onLoadArtifactMention={handleLoadArtifactMention}
            />
          </div>
        ) : (
          textInputs.map((input) => (
            <div
              key={input.prop}
              id={`AiModelInput__prompt--${input.prop}`}
              className={`flex flex-1 min-h-0 flex-col gap-1 px-3 ${!visualInputs.length ? "pt-3" : "pt-2"}`}>
              {textInputs.length > 1 && (
                <span className="text-[10px] text-secondary-foreground/70 px-0.5 select-none">
                  {input.name}
                  {input.required && (
                    <span className="text-destructive-foreground">*</span>
                  )}
                </span>
              )}
              <textarea
                id={`AiModelInput__promptInput--${input.prop}`}
                value={modelData?.[input.prop] ?? ""}
                onChange={(e) => updateModelData(input.prop, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleSubmit(false);
                  }
                }}
                placeholder="Type your prompt here & let's create something amazing..."
                className="placeholder:text-secondary-foreground/30 text-secondary-foreground text-xs/4 font-mono outline-hidden resize-none min-h-0 w-full flex-1 pb-10"
                rows={3}
              />
            </div>
          ))
        )}

        {/* Row 3: settings + actions */}
        <ActionBar
          isEditingArtifact={isEditingArtifact}
          loadedArtifact={loadedArtifact}
          hasLoadedArtifact={!!loadedArtifact}
          artifactHasChanges={artifactHasChanges}
          canSaveArtifact={!!artifactContent.trim()}
          artifactVisibility={artifactVisibility}
          onArtifactVisibilityChange={setArtifactVisibility}
          artifactScope={artifactScope}
          onArtifactScopeChange={setArtifactScope}
          onSaveArtifact={handleSaveArtifactChanges}
          onSaveArtifactAs={handleSaveArtifact}
          onCancelArtifact={handleCancelArtifact}
          onDeleteArtifact={handleDeleteArtifact}
          model={selectedModel}
          settings={settingInputs.map((input) => (
            <SettingInput
              key={input.prop}
              input={input}
              value={modelData?.[input.prop]}
              characters={characters}
              onChange={(value) => updateModelData(input.prop, value)}
              onEditVoiceSettings={async (current) => {
                const next = await openModal(
                  <VoiceSettingsModal voiceSettings={current} />,
                );
                if (next) updateModelData(input.prop, next);
              }}
            />
          ))}
          smartMode={smartMode}
          onSmartModeChange={setSmartMode}
          hasStructuringReasoning={!isEmpty(structuringReasoning)}
          onShowStructuringReasoning={handleOpenStructuringReasoning}
          submitting={submitting}
          previewing={previewing}
          canGenerate={
            !submitting &&
            !!selectedModel &&
            (smartMode || requiredFieldsFilled())
          }
          onGenerate={() => handleSubmit(false)}
          onSmartGenerate={() => handleSubmit(false)}
          onSmartPreview={handleSmartPreview}
          totalGenerations={totalGenerations}
          onTotalGenerationsChange={setTotalGenerations}
        />

        <ModelSelectorDialog
          open={modelsDialogOpen}
          onOpenChange={setModelsDialogOpen}
          models={models}
          selectedModel={selectedModel}
          onSelectModel={setSelectedModel}
        />
      </div>
    </div>
  );
});

export default AiModelInput;
